// Alpine controller for the property reviews CMS "review-picker" widget.
//
// The widget lets an operator choose which of the property's scraped review POOL
// to feature on the live reviews section, in a curated order. The selection is an
// ordered list of review ids (featured_review_ids).
//
// TRANSACTIONAL shell (working copy + pre-edit snapshot + ONE whole-value
// cms-image-changed on confirm) is shared with praise-picker and theme-quote-picker
// via createTransactionalPicker(); this file supplies only the review-specific pool
// type and available()/add()/remove() (+ MIN/byId/selectedReviews).
//
// PURE-DATA CORE: the POOL panel is click-to-add and the SELECTED panel is
// click-to-remove, so the widget is fully functional WITHOUT drag. SortableJS
// reorder (in the base) is layered ONLY on the selected list.

import { createTransactionalPicker, poolPickerMethods } from "./transactional-picker";
import { parseJsonOr } from "../shared/utils";

// One review from the property's scraped review pool. Keys match the
// components.CurationReview json tags (snake_case: author_country, highlight_quote).
// NOTE: no `themes` field — the producer omits it for this pool.
interface Review {
  id: string;
  author?: string;
  rating?: number;
  date?: string;
  text?: string;
  platform?: string;
  sentiment?: string;
  author_country?: string;
  praises?: string[];
  highlight_quote?: string;
}

interface CMSReviewPickerConfig {
  field: string;
  // Autosave routing key — threaded into the cms-image-changed detail so the
  // cmsEditor controller routes the write to the right section overlay.
  target: string;
  // Raw JSON strings from the widget ctx (parsed here so the templ never has to
  // build nested Alpine literals): pool = Review[], selected = string[] (the
  // ordered featured_review_ids).
  pool: string;
  selected: string;
}

// Hardcoded selection bounds — mirror the existing property reviews modal. The
// live reviews section renders any count; these are an editorial guardrail.
const MIN = 3;
const MAX = 10;

export function cmsReviewPicker(config: CMSReviewPickerConfig) {
  const pool = parseJsonOr(config.pool, [] as Review[]);
  const selected = parseJsonOr(config.selected, [] as string[]);

  return {
    ...createTransactionalPicker<string>({
      field: config.field,
      target: config.target,
      selected,
      max: MAX,
    }),
    // available()/add()/remove(): shared click-to-add/remove POOL logic. Reviews
    // are keyed by id and the search box filters author + text.
    ...poolPickerMethods<Review>({
      keyOf: (p) => p.id,
      matches: (p, q) =>
        (p.author || "").toLowerCase().includes(q) || (p.text || "").toLowerCase().includes(q),
    }),
    pool,
    MIN,

    // The pool record for an id (read-only display).
    byId(id: string): Review | undefined {
      return this.pool.find((p) => p.id === id);
    },

    // Ordered selected review objects for the SELECTED panel (drops any id no
    // longer in the pool so a stale id never renders a ghost row).
    selectedReviews(): Review[] {
      return this.selected.map((id) => this.byId(id)).filter((r): r is Review => !!r);
    },

    // Lower editorial guardrail (review-only; the base has no atMin).
    atMin(): boolean {
      return this.selected.length <= MIN;
    },
  };
}
